'use strict';

/** @typedef {import('../pipelineStatusTypes').PipelineStatusStage} PipelineStatusStage */

import '@xyflow/react/dist/style.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Background,
	Controls,
	MiniMap,
	MarkerType,
	Position,
	ReactFlow,
	ReactFlowProvider,
	useEdgesState,
	useNodesState,
	useReactFlow,
} from '@xyflow/react';
import { Box } from '@mui/material';
import {
	buildPipelineStatusGraphEdgesFromDependsOn,
	mapPipelineStatusStagesToGraphNodes,
	PIPELINE_STATUS_STAGE_NODE_TYPE,
} from './pipelineStatusGraphLayout';
import { PipelineStatusDrawer } from './PipelineStatusDrawer';
import { PipelineStatusStageNode } from './PipelineStatusStageNode';

/** @param {{ fitKey: string }} props */
function GraphFitView({ fitKey }) {
	const { fitView } = useReactFlow();
	useEffect(() => {
		const id = requestAnimationFrame(() => {
			fitView({ padding: 0.18, duration: 200 });
		});
		return () => cancelAnimationFrame(id);
	}, [fitView, fitKey]);
	return null;
}

const nodeTypes = { [PIPELINE_STATUS_STAGE_NODE_TYPE]: PipelineStatusStageNode };

/**
 * @param {{ stages: PipelineStatusStage[] }} props
 */
function PipelineStatusGraphInner({ stages }) {
	const [selected, setSelected] = useState(/** @type {PipelineStatusStage | null} */ (null));

	const baseNodes = useMemo(() => mapPipelineStatusStagesToGraphNodes(stages), [stages]);
	const [nodes, setNodes, onNodesChange] = useNodesState(baseNodes);
	useEffect(() => {
		setNodes(baseNodes);
	}, [baseNodes, setNodes]);

	const plainEdges = useMemo(() => buildPipelineStatusGraphEdgesFromDependsOn(stages), [stages]);
	const statusByTargetId = useMemo(() => {
		const m = new Map();
		for (const s of stages) m.set(s.id, s.status);
		return m;
	}, [stages]);

	const rfEdges = useMemo(
		() =>
			plainEdges.map((e) => ({
				...e,
				type: 'smoothstep',
				sourcePosition: Position.Right,
				targetPosition: Position.Left,
				animated: statusByTargetId.get(e.target) === 'running',
				markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b', width: 16, height: 16 },
				style: { stroke: '#94a3b8', strokeWidth: 1.5 },
			})),
		[plainEdges, statusByTargetId],
	);

	const [edges, setEdges, onEdgesChange] = useEdgesState(rfEdges);
	useEffect(() => {
		setEdges(rfEdges);
	}, [rfEdges, setEdges]);

	const fitKey = useMemo(
		() => stages.map((s) => `${s.id}:${s.status}:${s.factId || ''}:${s.occurredAt || ''}`).join('|'),
		[stages]
	);

	const onNodeClick = useCallback((/** @type {unknown} */ _evt, /** @type {{ data?: { stage?: PipelineStatusStage } }} */ node) => {
		const st = node?.data?.stage;
		setSelected(st ?? null);
	}, []);

	const minimapNodeColor = useCallback((/** @type {{ data?: { stage?: PipelineStatusStage } }} */ n) => {
		const st = n?.data?.stage?.status;
		switch (st) {
			case 'success':
				return '#2e7d32';
			case 'failed':
				return '#c62828';
			case 'running':
				return '#1565c0';
			default:
				return '#78909c';
		}
	}, []);

	return (
		<>
			<Box sx={{ height: 420, width: '100%', borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
				<ReactFlow
					nodes={nodes}
					edges={edges}
					onNodesChange={onNodesChange}
					onEdgesChange={onEdgesChange}
					nodeTypes={nodeTypes}
					onNodeClick={onNodeClick}
					nodesDraggable={false}
					nodesConnectable={false}
					edgesReconnectable={false}
					elementsSelectable
					proOptions={{ hideAttribution: true }}
					defaultEdgeOptions={{
						type: 'smoothstep',
						sourcePosition: Position.Right,
						targetPosition: Position.Left,
						markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b', width: 16, height: 16 },
					}}
					minZoom={0.4}
					maxZoom={1.5}
				>
					<Background gap={20} size={1} color="#e2e8f0" />
					<Controls showInteractive={false} />
					<MiniMap
						nodeStrokeWidth={2}
						nodeColor={minimapNodeColor}
						maskColor="rgba(15, 23, 42, 0.06)"
						style={{ height: 72, width: 110 }}
					/>
					<GraphFitView fitKey={fitKey} />
				</ReactFlow>
			</Box>
			<PipelineStatusDrawer stage={selected} open={Boolean(selected)} onClose={() => setSelected(null)} />
		</>
	);
}

/**
 * DAG visualization for pipeline status stages (pure graph view of `PipelineStatusStage[]`).
 * @param {{ stages: PipelineStatusStage[] }} props
 */
export function PipelineStatusGraph({ stages }) {
	return (
		<ReactFlowProvider>
			<PipelineStatusGraphInner stages={stages} />
		</ReactFlowProvider>
	);
}
